/**
 * Custom hook for manipulating tooth SVG elements based on props
 * Extracted from individual tooth components to eliminate duplication
 */

import { useEffect, RefObject, useRef } from 'react'
import {
  AbrasionConditions,
  AttritionConditions,
  BridgeConditions,
  CariesConditions,
  ClassMap,
  CrackConditions,
  CrownConditions,
  DriftedConditions,
  ErosionConditions,
  FillingConditions,
  FractureConditions,
  FurcationConditions,
  ImpactedConditions,
  InlayConditions,
  OnlayConditions,
  PeriradicularConditions,
  RecessionConditions,
  resolveAbrasionSeverityForView,
  SimpleConditions,
  ToothConfig,
  ToothView,
} from '../types/tooth.types'
import { OVERLAY_CLASSES, DISPLAY } from './overlay-constants'
import {
  getCrackClassName,
  getFractureVariantCandidates,
  handleSimpleOverlay,
  handleChildOverlay,
  handleChildOverlayWithMap,
  handleSurfaceOverlay,
  handleMaterialOverlay,
  handleCrownOverlay,
  handleErosionOverlay,
  handleBridgeOverlay,
  handleFurcationOverlay,
  handleInlayOverlay,
  handleOnlayOverlay,
  handlePeriradicularOverlay,
  handleSeverityOverlay,
} from './overlay-helpers'
import { omitAtomicsCoveredByCompounds } from '@utils/charting-surface-display.utils'
import { logger } from '@utils/safeLogger'

interface UseToothSVGManipulationProps {
  svgRef: RefObject<SVGSVGElement>
  view: ToothView
  conditions?: SimpleConditions
  erosion?: ErosionConditions
  caries?: CariesConditions
  recurringCaries?: CariesConditions
  inlay?: InlayConditions
  onlay?: OnlayConditions
  filling?: FillingConditions
  abrasion?: AbrasionConditions
  furcation?: FurcationConditions
  bridge?: BridgeConditions
  periradicular?: PeriradicularConditions
  fracture?: FractureConditions
  crack?: CrackConditions
  crown?: CrownConditions
  recession?: RecessionConditions
  attrition?: AttritionConditions
  impacted?: ImpactedConditions
  drifted?: DriftedConditions
  showMissingOverlay?: boolean
  showRetainedPrimaryOverlay?: boolean
  config: ToothConfig
  isLoaded?: boolean
}

const classMapSignatureCache = new WeakMap<ClassMap, string>()

const serializeClassMap = (classMap: ClassMap): string => {
  const cachedSignature = classMapSignatureCache.get(classMap)
  if (cachedSignature) {
    return cachedSignature
  }

  const signature = Object.keys(classMap)
    .sort()
    .map((key) => {
      const value = classMap[key]
      const normalizedValue = Array.isArray(value) ? value.join(',') : value
      return `${key}:${normalizedValue}`
    })
    .join('|')

  classMapSignatureCache.set(classMap, signature)
  return signature
}

const normalizeConditionValue = (
  value: string | string[] | boolean | undefined,
): string => {
  if (Array.isArray(value)) {
    return value.join(',')
  }

  return `${value ?? ''}`
}

const isInlineVisible = (element: SVGElement | null): element is SVGElement => {
  return !!element && element.style.display !== DISPLAY.NONE
}

/** Apply dental condition overlays on a tooth SVG from grouped props. */
export function useToothSVGManipulation({
  svgRef,
  view,
  conditions = {},
  erosion: erosionGroup,
  caries: cariesGroup,
  recurringCaries: recurringCariesGroup,
  inlay: inlayGroup,
  onlay: onlayGroup,
  filling: fillingGroup,
  abrasion: abrasionGroup,
  furcation: furcationGroup,
  bridge: bridgeGroup,
  periradicular: periradicularGroup,
  fracture: fractureGroup,
  crack: crackGroup,
  crown: crownGroup,
  recession: recessionGroup,
  attrition: attritionGroup,
  impacted: impactedGroup,
  drifted: driftedGroup,
  showMissingOverlay = true,
  showRetainedPrimaryOverlay = true,
  config,
  isLoaded = true,
}: UseToothSVGManipulationProps): void {
  const lastAppliedSignatureRef = useRef<string>('')

  useEffect(() => {
    if (!isLoaded) {
      lastAppliedSignatureRef.current = ''
      return
    }

    const svg = svgRef.current
    if (!svg) {
      return
    }

    const {
      missing = false,
      implant = false,
      rootCanal: root_canal = false,
      pulpotomy = false,
      pulpectomy = false,
      retainer = false,
      supernumerary = false,
      sinusTract = false,
      apicoetomy = false,
      periapical = false,
      veneer = false,
      veneerMaterial,
      sealants = false,
      retainedPrimary = false,
    } = conditions

    const {
      general: erosion = false,
      buccal: erosionBuccal = false,
      palatal: erosionPalatal = false,
      lingual: erosionLingual = false,
      occlusal: erosionOcclusal = false,
    } = erosionGroup || {}

    const caries = omitAtomicsCoveredByCompounds(cariesGroup?.surfaces || [])
    const cariesType = cariesGroup?.type
    const cariesRecurrent = cariesGroup?.recurrent || false

    const recurringCaries = omitAtomicsCoveredByCompounds(
      recurringCariesGroup?.surfaces || [],
    )
    const recurringCariesType = recurringCariesGroup?.type

    const inlay = omitAtomicsCoveredByCompounds(inlayGroup?.surfaces || [])
    const inlayMaterial = inlayGroup?.material
    const onlay = omitAtomicsCoveredByCompounds(onlayGroup?.surfaces || [])
    const onlayMaterial = onlayGroup?.material
    const filling = omitAtomicsCoveredByCompounds(fillingGroup?.surfaces || [])
    const fillingMaterial = fillingGroup?.material

    const abrasion = resolveAbrasionSeverityForView(abrasionGroup, view)

    const furcation = furcationGroup?.level?.toUpperCase() as
      | 'C1'
      | 'C2'
      | 'C3'
      | undefined
    const furcationAreas = furcationGroup?.areas

    const bridge = bridgeGroup?.connector
    const bridgeSource = bridgeGroup?.source
    const bridgeMaterial = bridgeGroup?.material

    const periradicularSeverity = periradicularGroup?.severity

    const fracture = fractureGroup?.types
    const fractureRecurrent = fractureGroup?.recurrent || false

    const crown = crownGroup?.material
    const recession = recessionGroup?.type
    const attrition = attritionGroup?.severity
    const impacted = impactedGroup?.direction
    const drifted = driftedGroup?.surfaces || []
    const crack = crackGroup?.types

    const {
      cariesClassMap,
      inlayClassMap,
      onlayClassMap,
      fillingClassMap,
      driftedClassMap,
    } = config

    const updateSignature = [
      normalizeConditionValue(caries),
      normalizeConditionValue(recurringCaries),
      normalizeConditionValue(inlay),
      normalizeConditionValue(onlay),
      normalizeConditionValue(filling),
      normalizeConditionValue(drifted),
      normalizeConditionValue(crack),
      normalizeConditionValue(fracture),
      normalizeConditionValue(cariesType),
      normalizeConditionValue(recurringCariesType),
      normalizeConditionValue(crown),
      normalizeConditionValue(inlayMaterial),
      normalizeConditionValue(onlayMaterial),
      normalizeConditionValue(fillingMaterial),
      normalizeConditionValue(bridge),
      normalizeConditionValue(bridgeSource),
      normalizeConditionValue(bridgeMaterial),
      normalizeConditionValue(veneerMaterial),
      normalizeConditionValue(periradicularSeverity),
      normalizeConditionValue(impacted),
      normalizeConditionValue(furcation),
      normalizeConditionValue(furcationAreas),
      normalizeConditionValue(attrition),
      normalizeConditionValue(abrasion),
      normalizeConditionValue(recession),
      `${cariesRecurrent}`,
      `${erosion}`,
      `${erosionBuccal}`,
      `${erosionPalatal}`,
      `${erosionLingual}`,
      `${erosionOcclusal}`,
      `${apicoetomy}`,
      `${periapical}`,
      `${root_canal}`,
      `${pulpotomy}`,
      `${pulpectomy}`,
      `${supernumerary}`,
      `${sinusTract}`,
      `${retainedPrimary}`,
      `${sealants}`,
      `${missing}`,
      `${showMissingOverlay}`,
      `${showRetainedPrimaryOverlay}`,
      `${implant}`,
      `${retainer}`,
      `${veneer}`,
      `${fractureRecurrent}`,
      serializeClassMap(cariesClassMap),
      serializeClassMap(inlayClassMap),
      serializeClassMap(onlayClassMap),
      serializeClassMap(fillingClassMap),
      serializeClassMap(driftedClassMap),
    ].join('::')

    if (lastAppliedSignatureRef.current === updateSignature) {
      return
    }

    let cancelled = false

    const frameId = window.requestAnimationFrame(() => {
      if (cancelled) {
        return
      }

      const currentSvg = svgRef.current
      if (!currentSvg || !currentSvg.isConnected) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            'SVG ref is null after timeout in useToothSVGManipulation',
          )
        }
        return
      }

      try {
        // ===== CARIES OVERLAY =====
        handleSurfaceOverlay(
          currentSvg,
          OVERLAY_CLASSES.CARIES_OVERLAY,
          OVERLAY_CLASSES.CARIES_SURFACE,
          caries,
          cariesClassMap,
          'caries-surface.',
          undefined,
          'material-type',
          cariesType
            ? { name: 'data-caries-type', value: cariesType }
            : undefined,
        )
        handleSurfaceOverlay(
          currentSvg,
          '[data-layer-sub="recurring-caries"]',
          OVERLAY_CLASSES.CARIES_SURFACE,
          recurringCaries,
          cariesClassMap,
          'caries-surface.',
          undefined,
          'material-type',
          recurringCariesType
            ? { name: 'data-caries-type', value: recurringCariesType }
            : undefined,
          'overlay-variants',
        )
        // Fallback for recurring overlays: some SVGs use variant names that do not
        // exactly map to the shared caries class map. If nothing was activated by the
        // helper, try direct data-layer-variant matching from surface codes.
        const recurringOverlays = Array.from(
          currentSvg.querySelectorAll<SVGElement>(
            '[data-layer-sub="recurring-caries"]',
          ),
        )
        if (recurringOverlays.length > 0 && recurringCaries.length > 0) {
          const recurringFallbackMap: Record<string, string[]> = {
            m: ['mesial'],
            d: ['distal'],
            b: ['buccal'],
            l: ['lingual'],
            p: ['palatal'],
            cb: ['cervicalbuccal', 'cervical-buccal'],
            o: ['occlusal'],
            i: ['incisal'],
            mb: [
              'mesio_buccal',
              'mesio-buccal',
              'mesio_buccal_cusp',
              'mesio-buccal-cusp',
            ],
            db: [
              'disto_buccal',
              'disto-buccal',
              'disto_buccal_cusp',
              'disto-buccal-cusp',
            ],
            mp: [
              'mesio_palatal',
              'mesio-palatal',
              'mesio_palatal_cusp',
              'mesio-palatal-cusp',
            ],
            dp: [
              'disto_palatal',
              'disto-palatal',
              'disto_palatal_cusp',
              'disto-palatal-cusp',
            ],
            mlc: [
              'mesio_lingual',
              'mesio-lingual',
              'mesio_lingual_cusp',
              'mesio-lingual-cusp',
            ],
            dlc: [
              'disto_lingual',
              'disto-lingual',
              'disto_lingual_cusp',
              'disto-lingual-cusp',
            ],
            mifp: ['miflp'],
            difp: ['diflp'],
            mifl: ['miflp'],
            difl: ['diflp'],
          }
          const getRecurringVariantCandidates = (
            surfaceCode: string,
          ): string[] => {
            const normalizedCode = surfaceCode.toLowerCase()
            const classMapValue = cariesClassMap[normalizedCode]
            const mapClasses = Array.isArray(classMapValue)
              ? classMapValue
              : classMapValue
                ? [classMapValue]
                : []
            const classDerived = mapClasses.flatMap((variantClass) => {
              const base = variantClass.toLowerCase()
              const hyphen = base.replace(/_/g, '-')
              const underscore = base.replace(/-/g, '_')
              return [
                base,
                hyphen,
                underscore,
                hyphen.replace(/-cusp$/g, ''),
                underscore.replace(/_cusp$/g, ''),
              ]
            })
            const staticDerived = recurringFallbackMap[normalizedCode] || []
            return Array.from(
              new Set([
                ...classDerived,
                ...staticDerived,
                ...classDerived.flatMap((name) => [
                  name.replace(/palatal/g, 'lingual'),
                  name.replace(/lingual/g, 'palatal'),
                ]),
                ...staticDerived.flatMap((name) => [
                  name.replace(/palatal/g, 'lingual'),
                  name.replace(/lingual/g, 'palatal'),
                ]),
              ]),
            )
          }

          recurringOverlays.forEach((recurringOverlay) => {
            const recurringVariants = Array.from(
              recurringOverlay.querySelectorAll<SVGElement>(
                '[data-layer-variant]',
              ),
            )
            const activeVariantNames = new Set(
              recurringCaries.flatMap((surfaceCode) =>
                getRecurringVariantCandidates(surfaceCode),
              ),
            )
            const recurringTypeValue = recurringCariesType?.toLowerCase()
            const recurringTypeFillColors: Record<string, string> = {
              d1: '#ffa7a1',
              d2: '#fe746b',
              d3: '#d8352a',
              e1: '#65acf5',
              e2: '#185dd8',
            }
            const recurringTypeFill = recurringTypeValue
              ? recurringTypeFillColors[recurringTypeValue]
              : undefined
            recurringVariants.forEach((variant) => {
              const variantName = (
                variant.getAttribute('data-layer-variant') || ''
              ).toLowerCase()
              const isActive = activeVariantNames.has(variantName)
              variant.style.display = isActive ? DISPLAY.BLOCK : DISPLAY.NONE
              if (isActive && recurringTypeValue) {
                variant.setAttribute('data-caries-type', recurringTypeValue)
                if (recurringTypeFill) {
                  variant
                    .querySelectorAll<SVGElement>(
                      'use, path, rect, polygon, ellipse',
                    )
                    .forEach((drawable) => {
                      drawable.setAttribute('fill', recurringTypeFill)
                    })
                }
              } else {
                variant.removeAttribute('data-caries-type')
              }
            })
            const hasVisibleRecurringVariantAfterFallback =
              recurringVariants.some(
                (variant) => variant.style.display !== DISPLAY.NONE,
              )
            recurringOverlay.style.display =
              hasVisibleRecurringVariantAfterFallback
                ? DISPLAY.BLOCK
                : DISPLAY.NONE
            recurringOverlay.classList.toggle(
              'active',
              hasVisibleRecurringVariantAfterFallback,
            )
          })
        } else if (recurringOverlays.length > 0) {
          recurringOverlays.forEach((recurringOverlay) => {
            recurringOverlay.style.display = DISPLAY.NONE
            recurringOverlay.classList.remove('active')
            recurringOverlay
              .querySelectorAll<SVGElement>('[data-layer-variant]')
              .forEach((variant) => {
                variant.style.display = DISPLAY.NONE
              })
          })
        }

        // Set opacity to 1 for recurrent caries paths, or restore default (0.5) when not recurrent
        let cariesOverlay = currentSvg.querySelector<SVGElement>(
          OVERLAY_CLASSES.CARIES_OVERLAY,
        )
        if (cariesOverlay) {
          cariesOverlay.querySelectorAll('path').forEach((path) => {
            if (cariesRecurrent) {
              ;(path as SVGElement).style.opacity = '1'
            } else {
              ;(path as SVGElement).style.opacity = '0.5'
            }
          })
        }

        // ===== INLAY OVERLAY =====
        handleInlayOverlay(currentSvg, inlay, inlayMaterial, inlayClassMap)

        // ===== ONLAY OVERLAY =====
        handleOnlayOverlay(currentSvg, onlay, onlayMaterial, onlayClassMap)

        // ===== EROSION OVERLAY =====
        handleErosionOverlay(
          currentSvg,
          erosion,
          erosionBuccal,
          erosionPalatal,
          erosionLingual,
          erosionOcclusal,
        )

        // ===== CROWN OVERLAY =====
        handleCrownOverlay(currentSvg, crown)

        // ===== VENEER OVERLAY =====
        handleMaterialOverlay(
          currentSvg,
          OVERLAY_CLASSES.VENEER_OVERLAY,
          'path, use, rect',
          veneer || Boolean(veneerMaterial),
          veneerMaterial,
        )

        // ===== PONTIC OVERLAY =====
        handleBridgeOverlay(currentSvg, bridge, bridgeMaterial)

        // ===== SEALANTS OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.SEALANT_OVERLAY,
          sealants,
        )

        // ===== MISSING OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.MISSING_OVERLAY,
          showMissingOverlay && !implant,
        )

        // ===== IMPLANT OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.IMPLANT_OVERLAY,
          implant,
        )

        // ===== HIDE BASE LAYER WHEN MISSING OR IMPLANT =====
        const baseLayerElements = currentSvg.querySelectorAll<SVGElement>(
          '[data-layer="base"]',
        )
        // Hide base layer for bridge types (ends with _bridge), but not for abutment types
        const isBridgeType =
          typeof bridge === 'string' &&
          (bridge.endsWith('_bridge') ||
            bridge === 'left_bridge' ||
            bridge === 'right_bridge' ||
            bridge === 'right_left_bridge')
        const isAbutmentType = bridgeSource === 'abutment'
        const shouldHideBaseLayer = missing || implant || isBridgeType
        baseLayerElements.forEach((el) => {
          // Some SVGs hide base via :has(structural active) CSS.
          // Force-show base for abutment states to keep tooth body visible.
          if (isAbutmentType) {
            el.style.display = DISPLAY.BLOCK
            return
          }
          el.style.display = shouldHideBaseLayer ? DISPLAY.NONE : ''
        })

        // ===== HIDE CONDITIONS AND ENDODONTRICS LAYERS =====
        const conditionsLayerElements = currentSvg.querySelectorAll<SVGElement>(
          '[data-layer="conditions"]',
        )
        const endodonticsLayerElements =
          currentSvg.querySelectorAll<SVGElement>('[data-layer="endodontics"]')

        // Determine what to hide based on missing, implant, and bridge states
        // Hide conditions for all bridge and abutment types
        const isAnyBridgeType =
          bridge === true ||
          (typeof bridge === 'string' &&
            (bridge.endsWith('_bridge') || bridge.endsWith('_abutment')))
        const shouldHideConditions = missing || implant || isAnyBridgeType

        // Hide endodontics for bridge types (not abutments) and missing/implant
        const shouldHideEndodontics =
          missing ||
          implant ||
          bridge === true ||
          (typeof bridge === 'string' && bridge.endsWith('_bridge'))

        // Apply visibility to conditions layer
        conditionsLayerElements.forEach((el) => {
          el.style.display = shouldHideConditions ? DISPLAY.NONE : ''
        })

        // Apply visibility to endodontics layer
        endodonticsLayerElements.forEach((el) => {
          el.style.display = shouldHideEndodontics ? DISPLAY.NONE : ''
        })

        // ===== FURCATION OVERLAY =====
        handleFurcationOverlay(currentSvg, furcation, furcationAreas)

        // ===== FILLING OVERLAY =====
        handleSurfaceOverlay(
          currentSvg,
          OVERLAY_CLASSES.FILLING_OVERLAY,
          OVERLAY_CLASSES.FILLING_SURFACE,
          filling,
          fillingClassMap,
          'filling-surface.',
          fillingMaterial,
          'material-type',
          undefined,
          'class-selector',
          false,
        )

        // ===== APICOETOMY OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.APICOETOMY_OVERLAY,
          apicoetomy,
        )

        // ===== PERIAPICAL OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.PERIAPICAL_OVERLAY,
          periapical,
        )

        // ===== ROOT CANAL OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.ROOT_CANAL_OVERLAY,
          root_canal,
        )

        // ===== PULPOTOMY OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.PULPOTOMY_OVERLAY,
          pulpotomy,
        )

        // ===== PULPECTOMY OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.PULPECTOMY_OVERLAY,
          pulpectomy,
        )

        // ===== PERIRADICULAR OVERLAY =====
        handlePeriradicularOverlay(currentSvg, periradicularSeverity)

        // ===== IMPACTED OVERLAY =====
        // SVGs use data-layer-variant="impacted-left" (etc.), not class impacted-overlay.left
        handleChildOverlay(
          currentSvg,
          OVERLAY_CLASSES.IMPACTED_OVERLAY,
          'g[class*="impacted-overlay"], [data-layer-variant]',
          impacted,
          '',
          (direction: string): string => `impacted-${direction}`,
        )

        // ===== DRIFTED OVERLAY =====
        handleChildOverlayWithMap(
          currentSvg,
          OVERLAY_CLASSES.DRIFTED_OVERLAY,
          'g[class*="drifted-overlay"], [data-layer-variant]',
          drifted,
          driftedClassMap,
          'drifted-overlay.',
        )

        // ===== SUPERNUMERARY OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.SUPERNUMERARY_OVERLAY,
          supernumerary,
        )

        // ===== SINUS TRACT OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.SINUS_TRACT_OVERLAY,
          sinusTract,
        )

        // ===== RETAINED PRIMARY OVERLAY =====
        handleSimpleOverlay(
          currentSvg,
          OVERLAY_CLASSES.RETAINED_PRIMARY_OVERLAY,
          showRetainedPrimaryOverlay && (retainedPrimary || retainer),
        )

        // ===== RECESSION OVERLAY =====
        handleChildOverlay(
          currentSvg,
          OVERLAY_CLASSES.RECESSION_OVERLAY,
          'g[class*="recession-overlay"], [data-layer-variant]',
          recession,
          'recession-overlay.',
        )

        // ===== ATTRITION OVERLAY =====
        handleSeverityOverlay(
          currentSvg,
          OVERLAY_CLASSES.ATTRITION_OVERLAY,
          attrition,
        )

        // ===== ABRASION OVERLAY =====
        handleSeverityOverlay(
          currentSvg,
          OVERLAY_CLASSES.ABRASION_OVERLAY,
          abrasion,
        )

        // ===== CRACK OVERLAY =====
        const crackOverlay =
          currentSvg.querySelector<SVGElement>(OVERLAY_CLASSES.CRACK_OVERLAY) ||
          currentSvg.querySelector<SVGElement>('[data-layer-sub="crack"]')
        if (crackOverlay) {
          const baseLayerUse = currentSvg.querySelector<SVGUseElement>(
            'g[data-layer="base"] use',
          )
          const baseHref = (
            baseLayerUse?.getAttribute('href') ||
            baseLayerUse?.getAttribute('xlink:href') ||
            ''
          ).toLowerCase()
          const currentView:
            | 'buccal'
            | 'palatal'
            | 'lingual'
            | 'occlusal'
            | 'unknown' = baseHref.includes('buccal')
            ? 'buccal'
            : baseHref.includes('palatal')
              ? 'palatal'
              : baseHref.includes('lingual')
                ? 'lingual'
                : baseHref.includes('occlusal')
                  ? 'occlusal'
                  : 'unknown'

          const isCrackTypeSupportedInView = (crackType: string): boolean => {
            if (currentView === 'unknown') {
              return true
            }
            if (crackType.includes('_buccal')) {
              return currentView === 'buccal'
            }
            if (crackType.includes('_palatal')) {
              return currentView === 'palatal'
            }
            if (crackType.includes('_lingual')) {
              return currentView === 'lingual'
            }
            return true
          }

          crackOverlay
            .querySelectorAll(
              `${OVERLAY_CLASSES.CRACK_AREA}, [data-layer-variant]`,
            )
            .forEach((el) => {
              ;(el as SVGElement).style.display = DISPLAY.NONE
            })

          const crackTypes = Array.isArray(crack) ? crack : crack ? [crack] : []

          if (crackTypes.length > 0) {
            crackTypes.forEach((crackType) => {
              if (!isCrackTypeSupportedInView(crackType)) {
                return
              }

              const crackClassName = getCrackClassName(crackType)
              if (!crackClassName) {
                return
              }
              const crackElement = crackOverlay.querySelector<SVGElement>(
                `${OVERLAY_CLASSES.CRACK_AREA}.${crackClassName}, [data-layer-variant="${crackClassName}"], [data-layer-variant="${crackClassName.replace(
                  /-/g,
                  '_',
                )}"]`,
              )
              if (crackElement) {
                crackElement.style.display = DISPLAY.BLOCK
              }
            })
            crackOverlay.style.display = DISPLAY.BLOCK
          } else {
            crackOverlay.style.display = DISPLAY.NONE
          }
        }

        // ===== FRACTURE OVERLAY =====
        const fractureOverlay =
          currentSvg.querySelector<SVGElement>(
            OVERLAY_CLASSES.FRACTURE_OVERLAY,
          ) ||
          currentSvg.querySelector<SVGElement>('[data-layer-sub="fracture"]')
        if (fractureOverlay) {
          // Hide all fracture areas first
          fractureOverlay
            .querySelectorAll(
              `${OVERLAY_CLASSES.FRACTURE_AREA}, [data-layer-variant]`,
            )
            .forEach((el) => {
              ;(el as SVGElement).style.display = 'none'
            })

          const fractureTypes = Array.isArray(fracture)
            ? fracture
            : fracture
              ? [fracture]
              : []

          if (fractureTypes.length > 0) {
            fractureTypes.forEach((fractureType) => {
              const fractureVariantCandidates =
                getFractureVariantCandidates(fractureType)
              if (fractureVariantCandidates.length === 0) {
                return
              }

              const fractureElements =
                fractureOverlay.querySelectorAll<SVGElement>(
                  fractureVariantCandidates
                    .map(
                      (candidate) =>
                        `${OVERLAY_CLASSES.FRACTURE_AREA}.${candidate}, [data-layer-variant="${candidate}"]`,
                    )
                    .join(', '),
                )
              fractureElements.forEach((fractureElement) => {
                fractureElement.style.display = DISPLAY.BLOCK
              })
            })
            fractureOverlay.style.display = DISPLAY.BLOCK
          } else {
            fractureOverlay.style.display = DISPLAY.NONE
          }
        }

        // Move caries/fracture overlays to be right after filling overlay if recurrent is true
        // This makes them appear on top of filling but below other overlays
        // If recurrent is false, restore them to their original position (before filling)
        const fillingOverlay = currentSvg.querySelector<SVGElement>(
          OVERLAY_CLASSES.FILLING_OVERLAY,
        )

        if (fillingOverlay && fillingOverlay.parentNode) {
          const parent = fillingOverlay.parentNode

          // Handle caries overlay positioning
          // Reuse cariesOverlay from outer scope if it exists, otherwise query for it
          if (!cariesOverlay) {
            cariesOverlay = currentSvg.querySelector<SVGElement>(
              OVERLAY_CLASSES.CARIES_OVERLAY,
            )
          }

          if (
            cariesOverlay &&
            cariesOverlay !== fillingOverlay &&
            cariesOverlay.parentNode === parent
          ) {
            const isVisible = isInlineVisible(cariesOverlay)

            if (isVisible) {
              // Get current positions
              const cariesIndex = Array.from(parent.children).indexOf(
                cariesOverlay,
              )
              const fillingIndex = Array.from(parent.children).indexOf(
                fillingOverlay,
              )

              // Move caries overlay after filling if recurrent, before filling if not recurrent
              const shouldBeAfterFilling =
                cariesRecurrent || recurringCaries.length > 0
              const isAfterFilling = cariesIndex > fillingIndex

              if (shouldBeAfterFilling !== isAfterFilling) {
                if (shouldBeAfterFilling) {
                  // Insert caries overlay right after filling overlay
                  const nextSibling = fillingOverlay.nextSibling
                  if (nextSibling) {
                    parent.insertBefore(cariesOverlay, nextSibling)
                  } else {
                    // If filling is last, append caries after it
                    parent.appendChild(cariesOverlay)
                  }
                } else {
                  // Insert caries overlay before filling overlay (original position)
                  parent.insertBefore(cariesOverlay, fillingOverlay)
                }
              }
            }
          }

          // Handle fracture overlay positioning
          const fractureOverlayForPositioning =
            currentSvg.querySelector<SVGElement>(
              OVERLAY_CLASSES.FRACTURE_OVERLAY,
            ) ||
            currentSvg.querySelector<SVGElement>('[data-layer-sub="fracture"]')

          if (
            fractureOverlayForPositioning &&
            fractureOverlayForPositioning !== fillingOverlay &&
            fractureOverlayForPositioning.parentNode === parent
          ) {
            const isVisible = isInlineVisible(fractureOverlayForPositioning)

            if (isVisible) {
              // Find overlays for positioning when recurrent (priority: crown > veneer > filling)
              const veneerOverlay = currentSvg.querySelector<SVGElement>(
                `${OVERLAY_CLASSES.VENEER_OVERLAY}, [data-layer-sub="veneer"]`,
              )
              const crownOverlay = currentSvg.querySelector<SVGElement>(
                `${OVERLAY_CLASSES.CROWN_OVERLAY}, g[data-layer="crown"]`,
              )

              // Get current positions
              const fractureIndex = Array.from(parent.children).indexOf(
                fractureOverlayForPositioning,
              )
              const fillingIndex = Array.from(parent.children).indexOf(
                fillingOverlay,
              )
              const veneerIndex = veneerOverlay
                ? Array.from(parent.children).indexOf(veneerOverlay)
                : -1
              const crownIndex = crownOverlay
                ? Array.from(parent.children).indexOf(crownOverlay)
                : -1

              // Determine where fracture should be
              // If recurrent: after crown (if present), then veneer (if no crown), then filling (if neither)
              // If not recurrent: before filling (original position)
              let referenceNode: Node | null = null

              if (fractureRecurrent) {
                // Priority: crown > veneer > filling
                if (crownOverlay && crownOverlay.parentNode) {
                  referenceNode = crownOverlay.nextSibling
                } else if (veneerOverlay && veneerOverlay.parentNode) {
                  referenceNode = veneerOverlay.nextSibling
                } else {
                  // If no crown or veneer, fallback to after filling
                  referenceNode = fillingOverlay.nextSibling
                }
              } else {
                // If not recurrent, fracture should be before filling (original position)
                referenceNode = fillingOverlay
              }

              // Check if fracture is in the correct position
              const isAfterVeneer =
                veneerOverlay && veneerIndex >= 0
                  ? fractureIndex > veneerIndex
                  : false
              const isAfterCrown =
                crownOverlay && crownIndex >= 0
                  ? fractureIndex > crownIndex
                  : false
              const isAfterFilling = fractureIndex > fillingIndex
              const isBeforeFilling = fractureIndex < fillingIndex

              // Determine if fracture needs to move
              let needsMove = false
              if (fractureRecurrent) {
                // If recurrent, should be after crown (if present), then veneer (if no crown), then filling (if neither)
                if (crownOverlay && crownIndex >= 0) {
                  needsMove = !isAfterCrown // Not in correct position if not after crown
                } else if (veneerOverlay && veneerIndex >= 0) {
                  needsMove = !isAfterVeneer // Not in correct position if not after veneer
                } else {
                  needsMove = !isAfterFilling // Not in correct position if not after filling
                }
              } else {
                // If not recurrent, should be before filling (original position)
                needsMove = !isBeforeFilling // Not in correct position if not before filling
              }

              if (needsMove) {
                const isValidReferenceNode =
                  referenceNode !== null && referenceNode.parentNode === parent

                if (isValidReferenceNode) {
                  parent.insertBefore(
                    fractureOverlayForPositioning,
                    referenceNode as ChildNode,
                  )
                } else {
                  // If no reference node, append to end
                  parent.appendChild(fractureOverlayForPositioning)
                }
              }
            }
          }
        }

        lastAppliedSignatureRef.current = updateSignature
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          logger.error('Error in useToothSVGManipulation:', error)
        }
      }
    })

    return () => {
      cancelled = true
      window.cancelAnimationFrame(frameId)
    }
  }, [
    view,
    conditions,
    erosionGroup,
    cariesGroup,
    recurringCariesGroup,
    inlayGroup,
    onlayGroup,
    fillingGroup,
    abrasionGroup,
    furcationGroup,
    bridgeGroup,
    periradicularGroup,
    fractureGroup,
    crackGroup,
    crownGroup,
    recessionGroup,
    attritionGroup,
    impactedGroup,
    driftedGroup,
    showMissingOverlay,
    showRetainedPrimaryOverlay,
    config,
    svgRef,
    isLoaded,
  ])
}
